import React from 'react';
import PropTypes from 'prop-types';
import { FormField as PackageFormField } from '@mobilelive-inc/telus-forms-core';
import { VulcanUserEditContext } from './userEditContext';
import {
  FieldInput,
  FieldSelect,
  FieldRadio,
  FieldCheckbox,
  FieldDate,
  FieldButton,
  FieldPlusMinusInput,
  FieldTextNote,
  FieldMultiLineText,
  FieldRichText,
  FieldMoney,
  FieldMultiCheckbox
} from '../fields';
import { Error } from '../../design/elements';
import FieldComplexLeadButton from '../fields/fieldComplexLeadButton';
import FieldFile from '../fieldFile';
import EditableSalesRepInfo from '../../design/shared/form/editableSalesRepInfo';
import FieldMultiFileUpload from '../fields/multiFileUpload';
import { VulcanSectionChromeContext } from './vulcanSectionChromeContext';

/**
 * Adapt a Vulcan field component's `onChange` argument to the plain VALUE the
 * engine's `setValue` expects. The local field components emit a DOM-style
 * event (`onChange(e)` → `e.target.{value|checked}`), mirroring the app's
 * `handleFormField(e.target, …)` contract (formStepVulcan.js), whereas a few
 * (Select/Date) already pass a bare value. Checkbox carries its state on
 * `checked`, every other input on `value` — the same split `handleFormField`
 * makes (`isCheckbox ? checked : value`).
 *
 * @param {*} arg    The argument the field component passed to `onChange`
 * @param {string} [type]
 * @returns {*} the extracted value
 */
function extractFieldValue(arg, type) {
  if (arg && arg.target) {
    if (type === 'Checkbox') return arg.target.checked;
    // FieldMultiFileUpload emits `{target: {name, fileUploded}}` (no `.value`)
    // — mapped to `true`/`''` rather than a raw boolean so the engine's own
    // `required` check (`isEmpty`, which treats `false` as non-empty) still
    // correctly flags "no file uploaded yet" as empty.
    if (type === 'MultiFileUpload') {
      return arg.target.fileUploded === true ? true : '';
    }
    return arg.target.value;
  }
  return arg;
}

/**
 * Vulcan's existing local field components (fieldInput.js, fieldSelect.js, ...)
 * read EVERYTHING — value, options, disabled, AND `onChange`/`onBlur` — from a
 * single `data` prop; they were never written against the package's
 * `NormalizedFieldProps` contract. The package's `FormField` dispatcher calls
 * `<Component {...field} type={type} data={data} />`.
 *
 * Two consumption paths flow through here:
 *  - Legacy direct dispatch (VulcanFormField with a hand-built `data` that
 *    already carries `onChange`): no top-level `onChange` prop is present, so we
 *    pass `data` through UNTOUCHED — byte-identical to formField.js (the parity
 *    the DOM-diff test locks in).
 *  - `<FormRunner>` (Stage 3): the engine puts the field's live value/disabled/
 *    options inside `data` (via the rich FieldState) but delivers `onChange`
 *    (and `error`) on the top-level `field` props — which the `data`-only
 *    components never read. Without bridging, `data.onChange` is undefined and
 *    the field throws on first input. So when a top-level `onChange` is present
 *    we fold a value-adapting `onChange`/`onBlur` into `data`, translating the
 *    component's event-style call into the engine's `setValue(name, value)`.
 *
 * `withError` also replicates the one behavior formField.js's dispatcher has
 * that the package's `FormField` lacks: a shared `<Error>` after the field,
 * read off the spread `field.error`.
 *
 * @param {import('react').ComponentType<any>} Component
 */
function withError(Component) {
  function FieldWithError({ error = '', ...rest }) {
    const { data, onChange } = rest;
    // Only fires on genuine USER interaction with the field — programmatic
    // writes (populate effects, fieldRules) never reach this bridge. That is
    // what makes it a correct "form was touched" signal; see userEditContext.
    const onUserEdit = React.useContext(VulcanUserEditContext);
    // FormRunner path: bridge the engine's value-based onChange into the `data`
    // object the field reads. Legacy path (no top-level onChange): pass through.
    const bridgedData =
      onChange && data
        ? {
            ...data,
            // FieldCheckbox renders from `checked`, not `value`
            // (`const isChecked = Boolean(checked)`), but the engine's state
            // carries only `value` — so `data.checked` was always `undefined`,
            // and `Boolean(undefined)` is always `false`. The click WAS being
            // stored correctly in engine state (confirmed:
            // `engine.values.agree === true` right after a click) — but the box
            // rendered permanently unchecked regardless, because FieldCheckbox
            // never read the value back. Real-data symptom: "individual
            // checkboxes whose selection is not persisting" — every click
            // "worked" and then visually reverted. Legacy kept the pair in
            // sync: `handleFormField` wrote
            // `{ value: isCheckbox ? checked : value, checked }`.
            ...(data.type === 'Checkbox'
              ? { checked: Boolean(data.value) }
              : {}),
            onChange: (e, refV) => {
              const nextValue = extractFieldValue(e, data.type);
              // Returning `false` vetoes the write — the engine equivalent of
              // legacy `handleFormField` bailing before `setFormFields` (e.g.
              // the access-points max alert).
              if (onUserEdit && onUserEdit(data.name, nextValue) === false) {
                return;
              }
              onChange(nextValue, refV);
            },
            onBlur: data.onBlur || (() => {})
          }
        : data;
    return (
      <>
        <Component {...rest} data={bridgedData} />
        {error && <Error text={error} />}
      </>
    );
  }
  FieldWithError.propTypes = {
    error: PropTypes.string
  };
  return FieldWithError;
}

/**
 * `MultiFileUpload` (real Contentful data, order 83480 step 4/Order Summary)
 * renders the SAME `FieldMultiFileUpload` widget formField.js's switch now
 * dispatches to (SOF-3442, Wireline Webforms Attachments Functionality) — but
 * that component takes `orderData` as a sibling prop, not nested inside
 * `data`, and the package's `FormField` dispatcher has no `orderData` prop of
 * its own to forward. This wrapper reads it off `VulcanSectionChromeContext`
 * (the same live channel `vulcanSectionRegistry.js`'s `useLiveOrderData` uses)
 * so the two dispatch paths stay in parity without threading a new prop
 * through the package.
 */
function VulcanMultiFileUpload({ data }) {
  const chrome = React.useContext(VulcanSectionChromeContext) || {};
  const orderData =
    (chrome.visibilityContext && chrome.visibilityContext.orderData) || null;
  return <FieldMultiFileUpload data={data} orderData={orderData} />;
}
VulcanMultiFileUpload.propTypes = {
  data: PropTypes.shape({}).isRequired
};

/**
 * The static half of Vulcan's field registry — a 1:1 port of formField.js's
 * type switch, for @mobilelive-inc/telus-forms-core's FormField dispatcher.
 * Covers every type formField.js handles; any OTHER unmapped type keeps
 * hitting the package's own `UnsupportedField` placeholder (FR-013).
 *
 * @type {import('@mobilelive-inc/telus-forms-core').FieldRegistry}
 */
export const standardVulcanFieldRegistry = {
  MultiFileUpload: withError(VulcanMultiFileUpload),
  Address: withError(FieldInput),
  AddressDetails: withError(FieldInput),
  Text: withError(FieldInput),
  Number: withError(FieldInput),
  Select: withError(FieldSelect),
  Radio: withError(FieldRadio),
  File: withError(FieldFile),
  Checkbox: withError(FieldCheckbox),
  MultiCheckbox: withError(FieldMultiCheckbox),
  Date: withError(FieldDate),
  Button: withError(FieldButton),
  PlusMinusInput: withError(FieldPlusMinusInput),
  Money: withError(FieldMoney),
  TextNote: withError(FieldTextNote),
  MultiLineText: withError(FieldMultiLineText),
  ComplexLeadButton: withError(FieldComplexLeadButton),
  RichText: withError(FieldRichText),
  EditSalesInfo: withError(EditableSalesRepInfo)
};

/**
 * Drop-in replacement for the local `formField.js` dispatcher, backed by the
 * package's `FormField` + `standardVulcanFieldRegistry`. Preserves the exact
 * pre-migration signature (`data`, plus `error` carried on `field.error`) and,
 * critically, its **null fallback**: the old `formField.js` `switch` rendered
 * `null` for any unmapped type (keeping only the error), whereas the package's
 * `FormField` would instead fall through to its own TDS default set (needing a
 * `BaseProvider`, and diverging for e.g. `LocationInput`). Guarding on the
 * registry here keeps unmapped types rendering exactly as before — every real
 * Vulcan field type is in `standardVulcanFieldRegistry`, so this branch only
 * affects already-dead content.
 *
 * @param {Object} props
 * @param {Object} props.data
 * @param {{error?: string}} [props.field]
 */
export function VulcanFormField({ data, field = undefined }) {
  const type = data && data.type;
  if (!standardVulcanFieldRegistry[type]) {
    // Parity with formField.js's `default: result = null` (error still shown).
    return field && field.error ? <Error text={field.error} /> : null;
  }
  return (
    <PackageFormField
      data={data}
      field={field}
      registry={standardVulcanFieldRegistry}
    />
  );
}
VulcanFormField.propTypes = {
  data: PropTypes.shape({ type: PropTypes.string }).isRequired,
  field: PropTypes.shape({ error: PropTypes.string })
};

export { withError };
